/**
 * contentValidators/explanationChecks.ts
 * Validates explanation quality: min length, generic text, forbidden openers,
 * didactic depth, jargon, passive voice, sentence length, rule references,
 * and check strategy requirements.
 */

import type { ValidationIssue } from '../../issueContract';
import { coach, info, blocker } from '../../issueContract';
import { getTierBand, getMinExplanationLength, getDistractorPattern, requiresRuleReference, getCheckStrategyMinTier } from '../../policyEngine';
import { isGenericText, isDidactic, GENERIC_EXPLANATION_PATTERNS, FORBIDDEN_OPENER_PATTERNS, CHECK_STRATEGY_PATTERN, TOPO_GEOGRAPHIC_DEPTH_PATTERN, detectJargon, hasPassiveVoice, detectLongSentences, getMaxWordsPerSentence } from '../../../utils';
import type { Question } from '../../../../content';
import { STAGE } from './shared';
import { SEVERITY } from '../../severityScale';

/**
 * Validate explanation quality for a question.
 */
export function validateExplanationQuality(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const tier = question.tier ?? 1;
  const module = question.module;
  const explanation = question.feedback?.explanationText?.trim() ?? '';
  if (!explanation) return issues;

  // Min length check
  const minExplanation = getMinExplanationLength(tier);
  if (explanation.length < minExplanation) {
    issues.push(coach('content', STAGE, 'CONTENT_EXPLANATION_TOO_SHORT', `Explanation (${explanation.length} chars) below minimum ${minExplanation} for tier ${tier}`, {
      field: 'feedback.explanationText',
      expected: `≥${minExplanation}`,
      actual: String(explanation.length),
      questionId: qId,
      familyId: fId,
      severity: tier >= 7 ? SEVERITY.NEAR_HIGH : SEVERITY.NEAR_REVIEW
    }));
  }

  // Generic check
  if (isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS)) {
    issues.push(blocker('content', STAGE, 'CONTENT_GENERIC_EXPLANATION', 'Explanation is generic/boilerplate — must explain the concept or calculation', {
      field: 'feedback.explanationText',
      questionId: qId,
      familyId: fId,
      severity: SEVERITY.HIGH
    }));
  }

  // ── Forbidden opener check ──────────────────────────────────────────────
  {
    for (const {
      pattern,
      label
    } of FORBIDDEN_OPENER_PATTERNS) {
      if (pattern.test(explanation)) {
        issues.push(coach('content', STAGE, 'CONTENT_FORBIDDEN_OPENER', `Explanation starts with forbidden opener "${label}" — rewrite to integrate naturally`, {
          field: 'feedback.explanationText',
          actual: label,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_MEDIUM,
          suggestion: `Remove the "${label}" opener and integrate the content naturally into the explanation`
        }));
      }
    }
  }

  // Didactic depth check (only if not generic)
  // TOPO EXEMPTION: Topo is knowledge-recall. If the explanation references
  // geographic facts (hasModuleSpecificDepth), that IS didactic for Topo.
  //
  // P3 CALIBRATION (mei 2026, GOVERNANCE_TAA_TRIAGE) — structure-first:
  //   Before falling back to the regex-based isDidactic() check, accept the
  //   explanation when structural didactic signals are present:
  //     - solutionSteps.length >= 2  -> the question itself ships a stepped
  //       solution; the explanation is a summary, not the teaching surface.
  //     - any supportAid of type 'rule' / 'rule_card' / 'strategy_steps'
  //       -> the rule reference is delivered visually, not lexically.
  //   Eliminates ~40 false positives on TAA reading-comprehension where the
  //   didactic depth lives in solutionSteps + rule cards, not the prose.
  if (!isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS) && explanation.length >= 30) {
    const isTopoWithGeographicDepth = module === 'Topo' && TOPO_GEOGRAPHIC_DEPTH_PATTERN.test(explanation);
    const stepCount = question.feedback?.solutionSteps?.length ?? 0;
    const hasStructuralSteps = stepCount >= 2;
    const hasRuleAid = (question.supportAids ?? []).some(aid => aid.type === 'rule' || aid.type === 'rule_card' || aid.type === 'strategy_steps');
    const hasStructuralDidactic = hasStructuralSteps || hasRuleAid;
    if (!isDidactic(explanation) && !isTopoWithGeographicDepth && !hasStructuralDidactic) {
      issues.push(coach('content', STAGE, 'CONTENT_EXPLANATION_NOT_DIDACTIC', 'Explanation lacks didactic depth — should show steps, reference rules, or explain reasoning', {
        field: 'feedback.explanationText',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MEDIUM
      }));
    }
  }

  // ── Jargon detection ────────────────────────────────────────────────────
  const jargonFound = detectJargon(explanation, tier);
  for (const {
    term,
    replacement
  } of jargonFound) {
    issues.push(coach('content', STAGE, 'CONTENT_JARGON_DETECTED', `Jargon "${term}" in explanation — too complex for children aged 9-11`, {
      field: 'feedback.explanationText',
      actual: term,
      questionId: qId,
      familyId: fId,
      severity: SEVERITY.LOW,
      suggestion: `Replace "${term}" with ${replacement}`
    }));
  }

  // ── Passive voice detection ─────────────────────────────────────────────
  if (hasPassiveVoice(explanation)) {
    issues.push(info('content', STAGE, 'CONTENT_EXPLANATION_PASSIVE', 'Explanation uses passive voice — use active voice ("kijk", "tel", "schrijf")', {
      field: 'feedback.explanationText',
      questionId: qId,
      familyId: fId,
      severity: SEVERITY.COSMETIC,
      suggestion: 'Replace passive constructions with active verbs'
    }));
  }

  // ── Sentence length check ───────────────────────────────────────────────
  const longSentences = detectLongSentences(explanation, tier);
  if (longSentences.length > 0) {
    const maxWords = getMaxWordsPerSentence(tier);
    issues.push(info('content', STAGE, 'CONTENT_SENTENCE_TOO_LONG', `${longSentences.length} sentence(s) exceed the ${maxWords}-word limit for tier ${tier}`, {
      field: 'feedback.explanationText',
      questionId: qId,
      familyId: fId,
      severity: SEVERITY.NEAR_MINOR,
      suggestion: `Keep sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`
    }));
  }

  // Module-specific rule reference
  //
  // GSR-26 (edtech-review 28 juni) — structure-first, consistent met de
  // CONTENT_EXPLANATION_NOT_DIDACTIC-check hierboven:
  //   De rule-ref-check vuurde voorheen tenzij de uitleg óf de module-pattern
  //   matchte óf isDidactic() was. Dat negeerde — anders dan de not-didactic
  //   check — de STRUCTURELE didactische signalen (solutionSteps ≥2, of een
  //   rule/rule_card/strategy_steps support-aid). Daardoor false-positiveerde
  //   CONTENT_MISSING_RULE_REF op de SWW/SPR-families waar de regel visueel
  //   (rule-card) of stapsgewijs (solutionSteps) geleverd wordt i.p.v. lexicaal
  //   in de prozatekst — precies de high-volume cluster uit de Code Registry.
  //   We hergebruiken dezelfde structural-escape die de not-didactic check al
  //   toepast: als de vraag de regel structureel levert, is de uitleg geen
  //   "missing rule ref". Pure false-positive-onderdrukking — geen severity/kind
  //   gewijzigd, geen emit-pad uitgeschakeld.
  if (module && requiresRuleReference(module)) {
    const pattern = getDistractorPattern(module);
    const stepCount = question.feedback?.solutionSteps?.length ?? 0;
    const hasStructuralSteps = stepCount >= 2;
    const hasRuleAid = (question.supportAids ?? []).some(aid => aid.type === 'rule' || aid.type === 'rule_card' || aid.type === 'strategy_steps');
    const hasStructuralDidactic = hasStructuralSteps || hasRuleAid;
    if (pattern && !pattern.test(explanation) && !isDidactic(explanation) && !hasStructuralDidactic) {
      issues.push(info('content', STAGE, 'CONTENT_MISSING_RULE_REF', `Explanation should reference module-specific rules for ${module}`, {
        field: 'feedback.explanationText',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MINOR
      }));
    }
  }

  // Check strategy requirement (SWW tier 7+)
  //
  // GSR-27 (edtech-review 28 juni) — structure-first, consistent met de
  // GSR-26 rule-ref/not-didactic escape (zelfde bestand, ~hierboven):
  //   De check-strategie ("controleer of de tijd/persoon klopt", "draai de zin
  //   om", "ik-vorm → hij-vorm", "Check: …") leeft op de SWW tier≥7 families
  //   meestal in een LETTERLIJKE solutionSteps-stap ("Check: vergelijk met …")
  //   of in een rule/strategy_steps support-aid ("Checkstrategie: …"), NIET in
  //   de prozatekst van explanationText. De check las echter alléén
  //   explanationText en false-positiveerde daardoor op precies die families
  //   die de strategie structureel leveren — de high-volume cluster uit de
  //   Code Registry (197×, allemaal SpellingWW tier≥7).
  //
  //   We evalueren CHECK_STRATEGY_PATTERN nu over de VOLLEDIGE didactische
  //   oppervlakte: explanationText + solutionSteps + rule/strategy_steps-aid-
  //   content. Er moet nog steeds een echte check-vocab-match aanwezig zijn
  //   ergens in die oppervlakte — dit is geen blanket-exemption (een vraag
  //   zónder enige controlestap blijft terecht vuren). Geen severity/kind
  //   gewijzigd (blijft coach / NEAR_MEDIUM), geen emit-pad uitgeschakeld:
  //   pure false-positive-onderdrukking, identiek van aard aan GSR-26.
  if (module) {
    const checkMinTier = getCheckStrategyMinTier(module);
    if (checkMinTier !== null && tier >= checkMinTier) {
      const solutionStepsText = (question.feedback?.solutionSteps ?? []).join(' ');
      const checkAidText = (question.supportAids ?? []).filter(aid => aid.type === 'rule' || aid.type === 'strategy_steps').map(aid => {
        const data = aid.data as {
          content?: string;
          title?: string;
        } | undefined;
        return `${data?.title ?? ''} ${data?.content ?? ''}`;
      }).join(' ');
      const checkSurface = `${explanation} ${solutionStepsText} ${checkAidText}`;
      if (!CHECK_STRATEGY_PATTERN.test(checkSurface)) {
        issues.push(coach('content', STAGE, 'CONTENT_MISSING_CHECK_STRATEGY', `Explanation for ${module} tier ≥${checkMinTier} should include a check strategy`, {
          field: 'feedback.explanationText',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_MEDIUM
        }));
      }
    }
  }
  return issues;
}